import { type CSSProperties, useId, useState } from 'react';
import { CATEGORIES, COLORS, IMPACT_LABELS, SUBCATEGORIES, TYPE_LABELS } from '../constants';
import { isRecentlyChanged } from '../itemDates';
import { safeHref } from '../links';
import { S } from '../strings';
import type { ParsedItem } from '../types';
import { BookmarkStar } from './BookmarkStar';
import { ImpactDot } from './ImpactDot';
import { MsTag } from './MsTag';
import { MuteToggle } from './MuteToggle';
import { TypeBadge } from './TypeBadge';

export function UpdateCard({
  item,
  style: extraStyle,
  bookmarked,
  onToggleBookmark,
  muted,
  onToggleMute,
  defaultExpanded = false,
}: {
  item: ParsedItem;
  style?: CSSProperties;
  bookmarked?: boolean;
  onToggleBookmark?: (item: ParsedItem) => void;
  // Only visible while muted when "Näytä kaikki" is on; rendered dimmed.
  muted?: boolean;
  onToggleMute?: (item: ParsedItem) => void;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const catLabel = CATEGORIES.find((c) => c.value === item.category)?.label ?? item.category;
  const catColor = COLORS.category[item.category] ?? '#ccc';
  const subLabel = (SUBCATEGORIES[item.category] ?? []).find((s) => s.value === item.subcategory)?.label;
  // Guard here, not in the badge, so an audience outside the known set (e.g.
  // a hand-built item) never renders an empty "Koskee" cell.
  const audienceLabel: string | undefined = item.audience
    ? S.card.audienceLabels[item.audience]
    : undefined;
  const analysisHeadingId = useId();

  return (
    <div
      className="card-hover fade-in"
      style={{
        display: 'block',
        background: 'var(--card-bg)',
        borderRadius: 10,
        overflow: 'hidden',
        marginBottom: 10,
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        border: expanded ? `2px solid ${catColor}` : '1px solid var(--border)',
        cursor: 'pointer',
        transition: 'border 0.2s, filter 0.2s',
        // Dim via `filter`, not `opacity`: the .fade-in animation runs with
        // fill-mode `forwards` and would pin opacity at 1 over an inline value.
        ...(muted ? { filter: 'opacity(0.5)' } : {}),
        ...extraStyle,
      }}
    >
      <div style={{ display: 'flex' }} onClick={() => setExpanded(!expanded)}>
        <div style={{ width: 4, flexShrink: 0, background: catColor }} />
        <div style={{ padding: '14px 16px', flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 8,
              flexWrap: 'wrap',
            }}
          >
            <TypeBadge type={item.updateType} />
            <ImpactDot impact={item.impact} />
            {item.actionNeeded && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#D13438',
                  background: '#FDE7E9',
                  padding: '2px 7px',
                  borderRadius: 3,
                }}
              >
                {S.card.actionNeeded}
              </span>
            )}
            <MsTag label={item.msTag} />
            <CountdownPill item={item} />
            <ChangedMarker item={item} />
            <span
              style={{
                fontSize: 11,
                color: 'var(--text-muted)',
                marginLeft: 'auto',
                flexShrink: 0,
              }}
            >
              {item.publishedDate ? new Date(item.publishedDate).toLocaleDateString('fi-FI') : ''}
            </span>
            {onToggleMute && (
              <MuteToggle active={!!muted} onToggle={() => onToggleMute(item)} />
            )}
            {onToggleBookmark && (
              <BookmarkStar
                active={!!bookmarked}
                onToggle={() => onToggleBookmark(item)}
              />
            )}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--text-primary)',
              lineHeight: 1.4,
              marginBottom: 6,
            }}
          >
            {item.title.replace(/^\[.*?\]\s*/, '')}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-secondary)',
              lineHeight: 1.5,
              ...(expanded
                ? {}
                : {
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }),
            }}
          >
            {item.summaryFI}
          </div>
          {!expanded && (
            <div
              style={{
                fontSize: 10,
                color: 'var(--text-muted)',
                marginTop: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <span>{item.sourceFeed}</span>
              <span style={{ marginLeft: 4, opacity: 0.5 }}>▼</span>
            </div>
          )}
        </div>
      </div>

      {expanded && (
        <div
          style={{
            borderTop: '1px solid var(--border)',
            padding: '16px 20px',
            background: 'var(--bg)',
            animation: 'fadeIn 0.2s ease',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px 20px',
              fontSize: 12,
              marginBottom: 14,
            }}
          >
            <InfoField label={S.card.category}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: catColor }} />
              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{catLabel}</span>
              {subLabel && (
                <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>› {subLabel}</span>
              )}
            </InfoField>
            <InfoField label={S.card.impact}>
              <ImpactDot impact={item.impact} />
              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                {IMPACT_LABELS[item.impact] ?? item.impact}
              </span>
            </InfoField>
            <InfoField label={S.card.type}>
              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                {TYPE_LABELS[item.updateType] ?? item.updateType}
              </span>
              {item.msTag && (
                <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: 11 }}>
                  ({item.msTag})
                </span>
              )}
            </InfoField>
            <InfoField label={S.card.publishedAt}>
              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                {item.publishedDate
                  ? new Date(item.publishedDate).toLocaleDateString('fi-FI', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })
                  : '-'}
              </span>
            </InfoField>
            <InfoField label={S.card.source}>
              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{item.sourceFeed}</span>
            </InfoField>
            <InfoField label={S.card.action}>
              <span
                style={{
                  fontWeight: 600,
                  color: item.actionNeeded ? '#D13438' : 'var(--text-primary)',
                }}
              >
                {item.actionNeeded ? S.card.actionLong : S.card.noAction}
              </span>
            </InfoField>
            {audienceLabel && (
              <InfoField label={S.card.audience}>
                <AudienceBadge label={audienceLabel} />
              </InfoField>
            )}
          </div>

          {/* The summary is already shown under the title, so the box only
              appears for the organisation analysis (new and changed items). */}
          {item.analysisFi && (
            <div
              role="group"
              aria-labelledby={analysisHeadingId}
              style={{
                background: 'var(--card-bg)',
                borderRadius: 8,
                padding: '12px 14px',
                fontSize: 13,
                lineHeight: 1.7,
                color: 'var(--text-primary)',
                border: '1px solid var(--border)',
                marginBottom: 14,
              }}
            >
              <h4
                id={analysisHeadingId}
                style={{
                  fontWeight: 700,
                  color: 'var(--text-muted)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  margin: '0 0 4px',
                  lineHeight: 1.4,
                }}
              >
                {S.card.analysisHeading}
              </h4>
              {item.analysisFi}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <a
              href={safeHref(item.sourceURL)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 18px',
                borderRadius: 8,
                border: 'none',
                background: catColor,
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                textDecoration: 'none',
              }}
            >
              {S.card.read}
            </a>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(false);
              }}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--card-bg)',
                color: 'var(--text-muted)',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {S.card.close}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontWeight: 700,
          color: 'var(--text-muted)',
          fontSize: 10,
          textTransform: 'uppercase',
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{children}</div>
    </div>
  );
}

// Who the change concerns (end users / IT admins / developers). Plain text
// label, neutral colours: the signal never relies on colour.
function AudienceBadge({ label }: { label: string }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: 'var(--text-primary)',
        background: 'var(--card-bg)',
        border: '1px solid var(--border)',
        padding: '2px 7px',
        borderRadius: 3,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

// "Päivitetty 16.10." marker: the daily fetch detected a content change in
// the last CHANGED_MARKER_DAYS days (clarito_lastchanged). Plain text, so the
// signal does not rely on colour; the tooltip adds the original publish date.
function ChangedMarker({ item }: { item: ParsedItem }) {
  if (!item.lastChanged || !isRecentlyChanged(item)) return null;
  const changed = new Date(item.lastChanged);
  const published = item.publishedDate ? new Date(item.publishedDate) : null;
  const publishedText =
    published && !isNaN(published.getTime()) ? published.toLocaleDateString('fi-FI') : '-';
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: '#005A9E',
        background: '#DEECF9',
        padding: '2px 7px',
        borderRadius: 3,
        whiteSpace: 'nowrap',
      }}
      title={S.card.changedTitle(changed.toLocaleDateString('fi-FI'), publishedText)}
    >
      {S.card.changed(changed.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric' }))}
    </span>
  );
}

// Days-to-go pill, shown next to the TypeBadge row when the item has a real
// parsed future effectiveDate. Rendering is gated on `effectiveDate > now` so
// items that fell through to the publishedDate fallback (always today-or-past)
// don't get a nonsensical pill. Same colour ramp as the Toimenpiteet hero so
// the visual signal is consistent across surfaces.
function CountdownPill({ item }: { item: ParsedItem }) {
  if (!item.effectiveDate) return null;
  const t = new Date(item.effectiveDate).getTime();
  if (isNaN(t)) return null;
  const days = Math.round((t - Date.now()) / 86_400_000);
  if (days <= 0) return null;
  const color =
    days <= 30
      ? COLORS.impact.critical
      : days <= 90
        ? COLORS.impact.high
        : days <= 180
          ? COLORS.impact.medium
          : COLORS.impact.low;
  return (
    <span
      style={{
        background: `${color}18`,
        color,
        fontSize: 10,
        fontWeight: 800,
        padding: '2px 7px',
        borderRadius: 8,
        whiteSpace: 'nowrap',
      }}
      title={`Voimaan ${new Date(item.effectiveDate).toLocaleDateString('fi-FI')}`}
    >
      {days} pv
    </span>
  );
}
